const idFields = ['productIds', 'varietyIds', 'priceIds', 'stockListIds', 'stockIds'];
export const uniqueIds = (values = []) => [...new Set(values.map(Number).filter((id) => Number.isSafeInteger(id) && id > 0))];

export const mergeCatalogEvents = (left, right = {}) => {
  const next = { ...left, ...right };
  idFields.forEach((field) => {
    const singular = field.slice(0, -1);
    next[field] = uniqueIds([...(left?.[field] || []), ...(right[field] || []), right[singular]]);
  });
  const hasScope = (event) => event && idFields.some((field) => event[field]?.length || event[field.slice(0, -1)]);
  next.full = Boolean(left?.full || right.full || (left && !hasScope(left)) || !hasScope(right));
  return next;
};

// Preserve every unchanged subtree, so mounted editors keep focus and drafts.
export const shareCatalogValue = (previous, next) => {
  if (previous === next) return previous;
  if (Array.isArray(next)) {
    if (!Array.isArray(previous)) return next;
    const keyed = new Map(previous.filter((item) => item?.id != null).map((item) => [String(item.id), item]));
    const result = next.map((item, index) => shareCatalogValue(item?.id != null ? keyed.get(String(item.id)) : previous[index], item));
    return result.length === previous.length && result.every((item, index) => item === previous[index]) ? previous : result;
  }
  if (!next || typeof next !== 'object' || !previous || typeof previous !== 'object') return next;
  const keys = Object.keys(next);
  const result = Object.fromEntries(keys.map((key) => [key, shareCatalogValue(previous[key], next[key])]));
  return keys.length === Object.keys(previous).length && keys.every((key) => result[key] === previous[key] || (key === 'priceSnapshot' && previous[key] && next[key])) ? previous : result;
};

export const mergeCatalogDelta = (previous = [], incoming = [], deltaProductIds) => {
  if (!Array.isArray(deltaProductIds)) return shareCatalogValue(previous, incoming);
  const replacements = new Map(incoming.map((product) => [String(product.id), product]));
  const affected = new Set(deltaProductIds.map(String));
  const result = previous.flatMap((product) => {
    const key = String(product.id);
    if (!replacements.has(key)) return affected.has(key) ? [] : [product];
    const replacement = shareCatalogValue(product, replacements.get(key));
    replacements.delete(key);
    return [replacement];
  });
  result.push(...replacements.values());
  return result.length === previous.length && result.every((product, index) => product === previous[index]) ? previous : result;
};

export const catalogEventParams = (event, products = []) => {
  if (!event || event.full) return {};
  const scoped = mergeCatalogEvents(null, event);
  if (scoped.full) return {};
  const priceIds = new Set(scoped.priceIds);
  const varietyIds = new Set(scoped.varietyIds);
  // Include known parents even when the server has just deleted the child.
  const productIds = uniqueIds([...scoped.productIds, ...products.filter((product) => (
    (product.Varieties || product.varieties || []).some((variety) => varietyIds.has(Number(variety.id))
      || (variety.VarietyPrices || variety.formats || []).some((price) => priceIds.has(Number(price.id))))
  )).map((product) => product.id)]);
  return Object.fromEntries(Object.entries({ productIds, varietyIds: scoped.varietyIds, priceIds: scoped.priceIds, stockListIds: scoped.stockListIds })
    .filter(([, values]) => values.length).map(([key, values]) => [key, values.join(',')]));
};

export const fetchCatalogDelta = async (load, event, products) => {
  const params = catalogEventParams(event, products);
  if (!Object.keys(params).length) return load({});
  if (Object.values(params).every((value) => value.split(',').length <= 200)) return load(params);
  const requests = Object.entries(params).flatMap(([key, value]) => {
    const values = value.split(',');
    const batches = [];
    for (let index = 0; index < values.length; index += 200) batches.push({ [key]: values.slice(index, index + 200).join(',') });
    return batches;
  });
  const data = new Map();
  const affected = new Set();
  let last;
  // Bounded sequential reads avoid saturating the database during large runs.
  for (const request of requests) {
    last = await load(request);
    (last?.data || []).forEach((product) => data.set(String(product.id), product));
    (last?.deltaProductIds || []).forEach((id) => affected.add(id));
  }
  return { ...last, data: [...data.values()], deltaProductIds: [...affected] };
};
